import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { hasPermission } from 'components/AuthorizationComponent';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ServerPaginatedTable } from 'components/table';
import { hyperMonitoringTypes } from 'constants/hyper-monitoring.constants';
import { permissionEnums } from 'constants/permission.constants';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { useConfirmationDialog } from 'hooks/shared/useConfirmationDialog';
import { HyperMonitoringDetailsDialog } from 'sections/HyperMonitoring/components/HyperMonitoringDetailsDialog';
import * as TableConfig from 'sections/HyperMonitoring/tablesConfig';
import {
	getHyperMonitoringAnalytics,
	getHyperMonitoringBeneficiaryDetails,
	markHyperMonitoringOperationDocumented,
	resendHyperMonitoringOperation,
} from 'services/hyper-monitoring.service';
import {
	clearDetails,
	getHyperMonitoringActionLoadingById,
	getHyperMonitoringDetailsState,
	getHyperMonitoringOperationsData,
} from 'state/reducers/hyper-monitoring.reducer';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { TableParagraph } from 'theme/elements/Paragraphs';
import type { HyperMonitoringOperationRecord } from 'types/hyper-monitoring.types';

type Props = {
	visibleTypes: hyperMonitoringTypes[];
	pages: Record<string, number>;
	onPageChange: (type: hyperMonitoringTypes, page: number) => Promise<void>;
	perPage: number;
};

export const HyperMonitoringTables: React.FC<Props> = ({ visibleTypes, pages, onPageChange, perPage }) => {
	const dispatch = useAppDispatch();
	const t = useAppSelector(selectTranslations);
	const requests = useAppSelector(getHyperMonitoringOperationsData);
	const detailsState = useAppSelector(getHyperMonitoringDetailsState);
	const actionLoadingById = useAppSelector(getHyperMonitoringActionLoadingById);
	const canReplay = hasPermission([permissionEnums.REPLAY_HYPER_OPERATION]);
	const canReadBeneficiary = hasPermission([permissionEnums.READ_BENEFICIARY]);
	const canManualReport = canReplay && canReadBeneficiary;
	const { dialogConfig, openDialog, handleConfirm, handleCancel } = useConfirmationDialog();
	const [selectedRow, setSelectedRow] = useState<HyperMonitoringOperationRecord | null>(null);
	const pagesRef = useRef(pages);
	pagesRef.current = pages;

	const closeDetails = useCallback(() => {
		setSelectedRow(null);
		dispatch(clearDetails());
	}, [dispatch]);

	useEffect(() => {
		if (!detailsState.shouldCloseAfterFailure) {
			return;
		}
		closeDetails();
	}, [detailsState.shouldCloseAfterFailure, closeDetails]);

	useEffect(() => {
		if (!selectedRow) {
			return;
		}

		const selectedRowStillExists = requests.data.some(
			(row: HyperMonitoringOperationRecord) => row.operationId === selectedRow.operationId
		);
		if (!selectedRowStillExists) {
			closeDetails();
		}
	}, [requests.data, selectedRow, closeDetails]);

	const handleResendClick = useCallback(
		(row: HyperMonitoringOperationRecord, event: React.MouseEvent<HTMLElement>) => {
			event.stopPropagation();

			const details = [
				{
					label: t('page.hyperMonitoring.resendConfirm.companyHebrewName'),
					value: row.companyHebrewName,
				},
				{
					label: t('page.hyperMonitoring.field.hyperAccountNumber'),
					value: row.hyperAccountNumber,
				},
				{
					label: t('page.hyperMonitoring.resendConfirm.beneficiaryName'),
					value: row.beneficiaryName,
				},
			];

			openDialog({
				title: t('page.hyperMonitoring.resendConfirm.title'),
				titleIcon: <img src="/icons/RefreshIcon.svg" width={44} height={44} alt="" />,
				content: (
					<Stack spacing={2} style={{ direction: 'rtl' }}>
						<Typography variant="body1">{t('page.hyperMonitoring.resendConfirm.message')}</Typography>
						<Box
							sx={{
								border: '1px solid',
								borderColor: 'divider',
								borderRadius: 1.5,
								overflow: 'hidden',
							}}
						>
							{details.map((detail, index) => (
								<Stack
									key={detail.label}
									direction="row"
									justifyContent="space-between"
									gap={2}
									sx={{
										px: 1.5,
										py: 1,
										bgcolor: index % 2 === 0 ? 'background.paper' : 'root.grayed',
									}}
								>
									<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
										{detail.label}
									</Typography>
									<Typography variant="body2" sx={{ textAlign: 'left', wordBreak: 'break-word' }}>
										{detail.value}
									</Typography>
								</Stack>
							))}
						</Box>
					</Stack>
				),
				confirmLabel: t('page.hyperMonitoring.resendConfirm.confirmButton'),
				cancelLabel: t('actions.cancel'),
				confirmColor: 'primary',
				onConfirm: async () => {
					const action = await dispatch(resendHyperMonitoringOperation(row.operationId));
					if (resendHyperMonitoringOperation.rejected.match(action)) {
						return;
					}
					dispatch(getHyperMonitoringAnalytics());
					await onPageChange(
						hyperMonitoringTypes.BENEFICIARIES,
						pagesRef.current[hyperMonitoringTypes.BENEFICIARIES] ?? 0
					);
				},
			});
		},
		[dispatch, openDialog, onPageChange, t]
	);

	const handleManualReportClick = useCallback(
		(row: HyperMonitoringOperationRecord, event: React.MouseEvent<HTMLElement>) => {
			event.stopPropagation();
			if (!canManualReport) {
				return;
			}
			setSelectedRow(row);
			dispatch(getHyperMonitoringBeneficiaryDetails(row));
		},
		[canManualReport, dispatch]
	);

	const handleDocumentedClick = useCallback(
		(row: HyperMonitoringOperationRecord) => {
			openDialog({
				title: t('page.hyperMonitoring.manualDocumentation.confirm.title'),
				titleIcon: <img src="/icons/WarningCircleIcon.svg" width={44} height={44} alt="" />,
				content: (
					<Stack spacing={2} style={{ direction: 'rtl' }}>
						<Typography variant="body1">
							{t('page.hyperMonitoring.manualDocumentation.confirm.message')}
						</Typography>
					</Stack>
				),
				confirmLabel: t('page.hyperMonitoring.manualDocumentation.confirm.confirmButton'),
				cancelLabel: t('page.hyperMonitoring.manualDocumentation.confirm.backButton'),
				confirmColor: 'primary',
				onConfirm: async () => {
					const action = await dispatch(markHyperMonitoringOperationDocumented(row.operationId));
					if (markHyperMonitoringOperationDocumented.rejected.match(action)) {
						return;
					}
					closeDetails();
					dispatch(getHyperMonitoringAnalytics());
					await onPageChange(
						hyperMonitoringTypes.BENEFICIARIES,
						pagesRef.current[hyperMonitoringTypes.BENEFICIARIES] ?? 0
					);
				},
			});
		},
		[closeDetails, dispatch, openDialog, onPageChange, t]
	);

	const columns = useMemo(
		() =>
			TableConfig.buildColumns(
				t,
				handleResendClick,
				handleManualReportClick,
				actionLoadingById,
				canReplay,
				canManualReport
			),
		[t, handleResendClick, handleManualReportClick, actionLoadingById, canReplay, canManualReport]
	);

	const handleBeneficiariesPageChange = useCallback(
		(page: number) => onPageChange(hyperMonitoringTypes.BENEFICIARIES, page),
		[onPageChange]
	);

	const beneficiariesServerPagination = useMemo(
		() => ({
			total: requests.pagination.total,
			hasMore: requests.pagination.hasMore,
			currentPage: pages[hyperMonitoringTypes.BENEFICIARIES] || 0,
			onPageChange: handleBeneficiariesPageChange,
			isLoading: requests.loading,
		}),
		[
			requests.pagination.total,
			requests.pagination.hasMore,
			pages[hyperMonitoringTypes.BENEFICIARIES],
			handleBeneficiariesPageChange,
			requests.loading,
		]
	);

	return (
		<Fragment>
			<Stack spacing={4}>
				{visibleTypes.map((type) => {
					const requestData =
						type === hyperMonitoringTypes.BENEFICIARIES
							? requests
							: { data: [], pagination: { total: 0, hasMore: false }, loading: false };
					const serverPagination =
						type === hyperMonitoringTypes.BENEFICIARIES
							? beneficiariesServerPagination
							: {
									total: 0,
									hasMore: false,
									currentPage: 0,
									onPageChange: handleBeneficiariesPageChange,
									isLoading: false,
							  };

					return (
						<Stack key={type} spacing={2}>
							<TableParagraph
								title={t('page.hyperMonitoring.table.title')}
								text={`${requestData.pagination.total} ${t('messages.requests')}`}
							/>
							<ServerPaginatedTable
								data={Array.isArray(requestData.data) ? requestData.data : []}
								columns={columns}
								perPage={perPage}
								noShrink
								serverPagination={serverPagination}
							/>
						</Stack>
					);
				})}
			</Stack>
			<HyperMonitoringDetailsDialog
				open={selectedRow !== null}
				row={selectedRow}
				detailsState={detailsState}
				actionLoading={selectedRow ? !!actionLoadingById[selectedRow.operationId] : false}
				onClose={closeDetails}
				onDocumentedClick={handleDocumentedClick}
			/>
			<ConfirmationDialog
				open={dialogConfig.open}
				title={dialogConfig.title}
				titleIcon={dialogConfig.titleIcon}
				message={dialogConfig.message}
				content={dialogConfig.content}
				confirmLabel={dialogConfig.confirmLabel}
				cancelLabel={dialogConfig.cancelLabel}
				onConfirm={handleConfirm}
				onCancel={handleCancel}
				loading={dialogConfig.loading}
				error={dialogConfig.error}
				confirmColor={dialogConfig.confirmColor}
			/>
		</Fragment>
	);
};
